import { projectSetup } from "../project-setup";

const supplementaryAreas = projectSetup?.website?.supplementary_areas;

//TODO move to and import form utils
const sumSunLightRanges = (arr) => {
	if (!arr || arr.length === 0) return 0;
	let sum = 0;
	arr.forEach((range) => {
		sum += range[1] - range[0];
	});
	return sum;
};

const arraysEquality = (arr1, arr2) => {
    if (!Array.isArray(arr1) || !Array.isArray(arr2)) return false;
    const set1 = new Set(arr1);
    const set2 = new Set(arr2);
    if (set1.size !== set2.size) return false;
    for (let item of set1) {
        if (!set2.has(item)) return false;
    }
    return true;
};
    
export const areFilterConfigsEqual = (obj1, obj2) => {
    const sortArray = (arr) => {
      if (!Array.isArray(arr)) return arr;
      if (arr.every(item => typeof item === 'string')) {
        return arr.slice().sort();
      }
      return arr;
    };
  
  if (obj1 === obj2) return true;
  if (obj1 == obj2) return true;

  if (obj1 == null || obj2 == null || typeof obj1 !== 'object' || typeof obj2 !== 'object') return false;

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);
  if (keys1.length !== keys2.length) return false;

  for (let key of keys1) {
    if (!keys2.includes(key)) return false;
    const val1 = sortArray(obj1[key]);
    const val2 = sortArray(obj2[key]);
    if (!areFilterConfigsEqual(val1, val2))return false;
  }
  return true;
};

export function getRoomsRange(apartments) {
	const rooms = apartments.map((apartment) => apartment.rooms);
	const minRooms = Math.min(...rooms);
	const maxRooms = Math.max(...rooms);
	return [minRooms, maxRooms];
}
//   - area (min-max range slider) maybe on slider labels floor min and ciel max to 1m2 precision
export function getAreaRange(apartments) {
	const areas = apartments.map((apartment) => apartment.area_original);
	const minArea = Math.min(...areas);
	const maxArea = Math.max(...areas);
	const roundedValues = [Math.floor(minArea), Math.ceil(maxArea)];
	return roundedValues;
}
//   - price (min-max range slider) - if prices are visible, maybe on slider labels floor min and ciel max to 10000 currency units precision, and step of 10000 currency units, also shorten label 100 tys. - 1,5 mln, TODO: handle different currency formats, localize 'tys.' and 'mln' to 'k' and 'm'?
function getPriceRange(apartments) {
	const prices = apartments.map((apartment) => apartment.price_brutto);
	const minPrice = Math.min(...prices);
	const maxPrice = Math.max(...prices);
	const roundedValues = [
		Math.floor(minPrice / 10000) * 10000,
		Math.ceil(maxPrice / 10000) * 10000,
	];
	return roundedValues;
}
//   - tiers (multiselect) - returns list of all unique tier ref_tier values
function getTiersList(apartments, tiers) {
	const levels = apartments.map(
		(apartment) =>
			tiers.find((tier) => tier.id_cad === apartment.id_cad.split('-')[2])
				.ref_tier
	); 
	const uniqueLevels = [...new Set(levels)].sort((a, b) => a - b);
	return uniqueLevels;
}
//   - availability (buttons available, reserved, sold) - if sold apartments are accessible, also handle error case if availability data is missing, disable sold button if sold apartments are not accessible, disable other buttons if no such apartments exist
function getAvailabilityTypes(apartments) {
	const types = new Set();
	apartments.forEach((apartment) => {
		types.add(apartment.availability);
	});
	return Array.from(types);
}
//   - edifices (buttons for each edifice) - if multiple edifices, but if many edifices consider some other UI element like dropdown? multiselect? still 17 items even in multiselect is too much - any other way?
function getBuildingIds(apartments) {
	const ids = new Set();
	apartments.forEach((apartment) => {
		ids.add(apartment.id_cad.split('-')[0]);
	});
	return Array.from(ids);
}
//   - special labels (on offer, last units, turnkey, etc) - if such data exists, only render applicable buttons
//   - exposition (buttons for each direction N E W S) - if such data exists, localize direction labels
function getExpositionTypes(apartments) {
    const directionOrder = ['S', 'W', 'N', 'E'];
	const types = new Set();
	apartments.forEach((apartment) => {
		apartment.exposition.forEach((direction) => types.add(direction));
	});
    const array = Array.from(types);
    return array.sort((a, b) => directionOrder.indexOf(a) - directionOrder.indexOf(b));
}
//   - insolation (multiselect with grouped ranges) - returns list of grouped ranges based on total insolation hours
function getInsolationRanges(apartments) {
	const insolationValues = apartments.map((apartment) =>
		sumSunLightRanges(apartment.sun_light_ranges)
	);
	const minInsolation = Math.min(...insolationValues);
	const maxInsolation = Math.max(...insolationValues);
	
	// Jeśli wszystkie apartamenty mają 0 (brak danych o nasłonecznieniu), zwróć pusty array
	if (maxInsolation === 0) {
		return [];
	}
	
	const allRanges = [
		[0, 3],
		[3, 7],
		[7, 10],
	];
	
	const applicableRanges = [];
	allRanges.forEach(r => {
		if (r[0] <= maxInsolation && minInsolation <= r[1]){
			applicableRanges.push(r);
		}
	});
	
	return applicableRanges;
}
//   - supplementary areas (buttons for balcony, loggia, terrace, garden) - if such data exists
function getSupplementaryAreaTypes(apartments) {
	const types = new Set();
	apartments.forEach((apartment) => {
		apartment.compartments.forEach((c) => {
            supplementaryAreas.forEach(type => {
                if (c.name_cad === type) types.add(type);
            });
		});
	});
	return Array.from(types);
}
//   - can we think of any other useful filters?

/**
 * Get initial filter configuration.
 * @returns {Object} Initial filter configuration object with all-inclusive values for various apartment attributes.
 * rooms: [minRooms, maxRooms]
 * area: [minArea, maxArea]
 * price: [minPrice, maxPrice]
 * tiers: array of all unique tier ref_tier values
 * insolation: array of grouped insolation ranges like [[0,3], [3,6], [6,9], [9,10]]
 * buildings: array of all building id_cad values
 * supplements: array of all unique supplementary areas from apartments
 * availability: array of all unique availability statuses from apartments
 * exposition: array of all unique expositions from apartments
 * strictSupplementsMatch: boolean, default false
 * strictExpositionMatch: boolean, default false
 */
export const getInitialFiltersConfig = ({ apartments, tiers }) => {
	const allAvailabilityTypes = getAvailabilityTypes(apartments);
	// Domyślnie wykluczamy 'sold' z initial config - to będzie punkt odniesienia dla filtrów
	const defaultAvailability = allAvailabilityTypes.filter(type => type !== 'sold');
	
	return {
		rooms: getRoomsRange(apartments),
		area: getAreaRange(apartments),
		price: getPriceRange(apartments),
		tiers: getTiersList(apartments, tiers),
		insolation: getInsolationRanges(apartments),
		buildings: getBuildingIds(apartments),
		availability: defaultAvailability,
		exposition: getExpositionTypes(apartments),
		strictSupplementsMatch: false,
		strictExpositionMatch: false,
		supplements: getSupplementaryAreaTypes(apartments),
	};
};

/**
 * Filters and sorts a list of apartments based on the provided configuration.
 * @param {*} data - The initial apartment data.
 * @param {*} filterConfig - The configuration for filtering the apartments.
 * @param {*} sortConfig - The configuration for sorting the apartments.
 */
export const filterAndSortApartmentList = ({
	data,
	filtersConfig,
	sortConfig,
}) => {
	let filteredData = [...data];
	// console.log(`Initial apartments: ${filteredData.length}`);
	Object.entries(filtersConfig).forEach(([key, value]) => {
		if (value == null) return;
		let beforeLength = filteredData.length;
		switch (key) {
			case 'unitTypes':
				// console.log(`Before unitTypes filter: ${filteredData.length}`);
				filteredData = filteredData.filter(a => value.includes(a.id_cad.split('-')[3]));
				// console.log(`After unitTypes filter: ${filteredData.length}, filtered out: ${beforeLength - filteredData.length}`);
				break;
			case 'area':
				// console.log(`Before area filter: ${filteredData.length}`);
				filteredData = filteredData.filter(
					(a) =>
						a.area_original >= value[0] &&
						Math.floor(a.area_original) <= value[1]
				);
				// console.log(`After area filter: ${filteredData.length}, filtered out: ${beforeLength - filteredData.length}`);
				break;
			case 'availability':
				// console.log(`Before availability filter: ${filteredData.length}`);
				filteredData = filteredData.filter((a) =>
					value.includes(a.availability)
				);
				// console.log(`After availability filter: ${filteredData.length}, filtered out: ${beforeLength - filteredData.length}`);
				break;
			case 'buildings':
				// console.log(`Before buildings filter: ${filteredData.length}`);
				filteredData = filteredData.filter((a) =>
					value.some((v) => v.includes(a.building_id))
				);
				// console.log(`After buildings filter: ${filteredData.length}, filtered out: ${beforeLength - filteredData.length}`);
				break;
			case 'exposition':
				// console.log(`Before exposition filter: ${filteredData.length}`);
				if (filtersConfig.strictExpositionMatch) {
					filteredData = filteredData.filter((a) =>
						arraysEquality(a.exposition, value)
					);
				} else {
					filteredData = filteredData.filter((a) =>
						value.some((v) => a.exposition.includes(v))
					);
				}
				// console.log(`After exposition filter: ${filteredData.length}, filtered out: ${beforeLength - filteredData.length}`);
				break;
			case 'insolation':
				// console.log(`Before insolation filter: ${filteredData.length}`);
				// Jeśli value jest puste (brak danych o nasłonecznieniu), pomiń filtrowanie
				if (value.length === 0) {
					console.log(`Insolation filter skipped: no ranges available`);
					break;
				}
				filteredData = filteredData.filter((a) => {
					const totalInsolation = sumSunLightRanges(a.sun_light_ranges);
					// Check if total insolation falls within any of the selected ranges
					return value.some(range => 
						totalInsolation >= range[0] && totalInsolation <= range[1]
					);
				});
				// console.log(`After insolation filter: ${filteredData.length}, filtered out: ${beforeLength - filteredData.length}`);
				break;
			case 'price':
				// console.log(`Before price filter: ${filteredData.length}`);
				filteredData = filteredData.filter(
					(a) => a.price_brutto >= value[0] && a.price_brutto <= value[1]
				);
				// console.log(`After price filter: ${filteredData.length}, filtered out: ${beforeLength - filteredData.length}`);
				break;
			case 'supplements':
				// console.log(`Before supplements filter: ${filteredData.length}`);
				if (filtersConfig.strictSupplementsMatch) {
					filteredData = filteredData.filter((a) =>
						arraysEquality(
                            a.compartments.map(
                            (c) => c.name_cad).filter(
                                name => supplementaryAreas.includes(name)
                            ),
                            value
						)
					);
				} else {
                    // Non-strict: mieszkanie ma przynajmniej jeden zaznaczony suplement LUB nie ma żadnych suplementów
                    filteredData = filteredData.filter((a) => {
                        const apartmentSupplements = a.compartments.map((c) => c.name_cad).filter(name => supplementaryAreas.includes(name));
                        const hasAnySelected = value.some((v) => apartmentSupplements.includes(v));
                        const hasNoSupplements = apartmentSupplements.length === 0;
                        return hasAnySelected || hasNoSupplements;
                    });
				}
				// console.log(`After supplements filter: ${filteredData.length}, filtered out: ${beforeLength - filteredData.length}`);
				break;
			case 'rooms':
				// console.log(`Before rooms filter: ${filteredData.length}`);
				filteredData = filteredData.filter(
					(a) => a.rooms >= value[0] && a.rooms <= value[1]
				);
				// console.log(`After rooms filter: ${filteredData.length}, filtered out: ${beforeLength - filteredData.length}`);
				break;
			case 'tiers':
				// console.log(`Before tiers filter: ${filteredData.length}`);
				filteredData = filteredData.filter((a) =>
					value.includes(parseInt(a.id_cad.split('-')[2]))
				);
				// console.log(`After tiers filter: ${filteredData.length}, filtered out: ${beforeLength - filteredData.length}`);
				break;
			default:
				break;
		}
	});

    if (sortConfig.key) {
        filteredData.sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];
            
            // Handle special cases
            if (sortConfig.key === 'price_brutto') {
                aValue = parseFloat(aValue) || 0;
                bValue = parseFloat(bValue) || 0;
            }
            
            if (aValue < bValue)
                return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue)
                return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    }

	return filteredData;
};
